import { type MutableRefObject, useMemo } from 'react'

import { F0Box } from '@factorialco/f0-react'
import type { F0FormRef } from '@factorialco/f0-react'

import type {
  ProcurementValueObjectsPurchaseOrderStatusEnum,
  PurchaseOrderDetailFieldsFragment,
} from 'generated/resources/graphql'

import type { EditCallbacks } from 'modules/procurement/components/purchaseOrders/PurchaseOrderDetail'
import type { LineItemDraft } from 'modules/procurement/components/purchaseOrders/PurchaseOrderDetail/useEditPurchaseOrder'
import { groupHeaderFields } from 'modules/procurement/components/purchaseOrders/PurchaseOrderDetail/utils'

import HeaderSection from './HeaderSection'
import LineItemsSection from './LineItemsSection'
import NotesDeliverySection from './NotesDeliverySection'
import PurchaseOrderEditForm from './PurchaseOrderEditForm'

interface Props {
  purchaseOrder: PurchaseOrderDetailFieldsFragment
  locale: string
  companyId: string
  isEditing?: boolean
  formRef?: MutableRefObject<F0FormRef | null>
  lineItemsRef?: MutableRefObject<LineItemDraft[]>
  lineItems?: LineItemDraft[]
  editCallbacks?: EditCallbacks
  onEditSuccess?: () => void
  targetStatus?: ProcurementValueObjectsPurchaseOrderStatusEnum
}

function DetailsTab({
  purchaseOrder,
  locale,
  companyId,
  isEditing = false,
  formRef,
  lineItemsRef,
  lineItems,
  editCallbacks,
  onEditSuccess,
  targetStatus,
}: Props) {
  const headerFieldsBySection = useMemo(() => {
    const definitions = purchaseOrder.poTemplateVersion?.poTemplateFieldDefinitions ?? []
    const values = purchaseOrder.poFieldValues
    return groupHeaderFields(definitions, values)
  }, [purchaseOrder.poTemplateVersion, purchaseOrder.poFieldValues])

  const generalInfoFields = headerFieldsBySection.get('general_information') ?? []
  const vendorContactFields = headerFieldsBySection.get('vendor_contact') ?? []
  const notesDeliveryFields = headerFieldsBySection.get('notes_and_delivery') ?? []

  return (
    <F0Box padding="lg">
      <F0Box display="flex" flexDirection="column" gap="2xl">
        {isEditing && formRef && lineItemsRef && editCallbacks && onEditSuccess ? (
          <PurchaseOrderEditForm
            purchaseOrder={purchaseOrder}
            companyId={companyId}
            locale={locale}
            lineItemsRef={lineItemsRef}
            lineItems={lineItems ?? []}
            formRef={formRef}
            editCallbacks={editCallbacks}
            onSuccess={onEditSuccess}
            targetStatus={targetStatus}
          />
        ) : (
          <>
            <HeaderSection
              section="general_information"
              fields={generalInfoFields}
              purchaseOrder={purchaseOrder}
            />
            <HeaderSection
              section="vendor_contact"
              fields={vendorContactFields}
              purchaseOrder={purchaseOrder}
            />
            <LineItemsSection purchaseOrder={purchaseOrder} locale={locale} />
            <NotesDeliverySection fields={notesDeliveryFields} purchaseOrder={purchaseOrder} />
          </>
        )}
      </F0Box>
    </F0Box>
  )
}

export default DetailsTab
